import { WebsiteData } from '../types/website'
import { getBusinessHoursStatus } from '../utils/businessHours'
import { DEFAULT_THEME_CSS } from './DefaultChrome'
import {
  ThemeShellProps,
  DEFAULT_HERO_IMAGE,
  telHref,
  mapsHref,
  formatPhone,
  brandLetter,
  PhoneGlyph,
  PinGlyph,
  RelayFooter,
} from './themeShared'

/**
 * "Red Alert" theme — dark emergency-response. Graphite chrome with SIREN-RED
 * accents, a hazard-chevron "24/7 Emergency" band, condensed caps. Reads like a
 * 24/7 emergency line. Dark chrome + LIGHT content (nothing breaks).
 */

/** Default copy for the scrolling emergency band when the owner sets none. */
export const DEFAULT_EMERGENCY_BANNER = '24/7 Emergency Towing & Recovery'

/** How many times the band's text repeats — enough to fill the seamless loop. */
const MARQUEE_SEGMENTS = Array.from({ length: 8 })

function RedAlertHero({ siteData }: { siteData: WebsiteData }) {
  const { title, phoneNumber, address, coverPhoto, hours } = siteData
  const image = coverPhoto || DEFAULT_HERO_IMAGE
  const status = hours && hours.trim() ? getBusinessHoursStatus(hours, address) : null

  return (
    <section className="ra-hero">
      <img
        className="ra-hero-bg"
        src={image}
        alt=""
        aria-hidden="true"
        onError={(e) => {
          const t = e.target as HTMLImageElement
          if (t.src !== DEFAULT_HERO_IMAGE) t.src = DEFAULT_HERO_IMAGE
        }}
      />
      <span className="ra-hero-scrim" aria-hidden="true" />
      <div className="ra-hero-inner">
        <span className="ra-eyebrow">
          <i className="ra-dot" aria-hidden="true" />
          {status?.isOpen ? 'Open now · Emergency line' : 'Emergency line · 24/7'}
        </span>
        <h1 className="ra-hero-title">{title || 'Towing & Recovery'}</h1>
        <p className="ra-hero-sub">Emergency towing &amp; recovery, response when you need it most{address ? ` — ${address}` : ''}.</p>
        <div className="ra-hero-cta">
          {phoneNumber && (
            <a className="ra-call" href={telHref(phoneNumber)}>
              <PhoneGlyph size={22} />
              <span className="ra-call-stack"><small>Call now · 24/7</small><strong>{formatPhone(phoneNumber)}</strong></span>
            </a>
          )}
          {address && (
            <a className="ra-ghost" href={mapsHref(address)} target="_blank" rel="noopener noreferrer">
              <PinGlyph /> Get directions
            </a>
          )}
        </div>
      </div>
    </section>
  )
}

export function RedAlertShell({
  siteData, tabs, activeTab, onTabChange, siteIcon, showHero, isPreview, brand, emergencyBanner, children,
}: ThemeShellProps) {
  const { title, phoneNumber } = siteData
  const letter = brandLetter(title)
  const bannerText = emergencyBanner?.trim() || DEFAULT_EMERGENCY_BANNER

  return (
    <div className="rt-site rt-theme--redalert" style={{ ['--rt-brand' as string]: brand, minHeight: isPreview ? undefined : '100vh' }}>
      <style>{DEFAULT_THEME_CSS}</style>
      <style>{REDALERT_CSS}</style>

      <header className="ra-nav" role="banner">
        <div className="ra-nav-inner">
          <button className="ra-brand" onClick={() => onTabChange(tabs[0]?.id)} aria-label={title || 'Home'}>
            <span className="ra-brand-tile">{siteIcon ? <img src={siteIcon} alt="" /> : letter}</span>
            <span className="ra-brand-name">{title || 'Towing'}</span>
          </button>
          <nav className="ra-links" aria-label="Sections">
            {tabs.map((t) => (
              <button key={t.id} className={`ra-link${t.id === activeTab ? ' is-active' : ''}`} onClick={() => onTabChange(t.id)}>
                {t.label}
              </button>
            ))}
          </nav>
          {phoneNumber && (
            <a className="ra-nav-call" href={telHref(phoneNumber)}>
              <PhoneGlyph size={17} /> {formatPhone(phoneNumber)}
            </a>
          )}
        </div>
        <div className="ra-alertbar" aria-hidden="true">
          <span className="ra-alertbar-track">
            {MARQUEE_SEGMENTS.map((_, i) => (
              <span key={i}>{bannerText}&nbsp;&nbsp;›››&nbsp;&nbsp;</span>
            ))}
          </span>
        </div>
      </header>

      <div className="ra-content">
        {showHero && <RedAlertHero siteData={siteData} />}
        <main className="ra-main">
          <div className="rt-page" key={activeTab}>{children}</div>
        </main>

        <RelayFooter />
      </div>

      {phoneNumber && (
        <a className="ra-callbar" href={telHref(phoneNumber)}>
          <PhoneGlyph size={20} /><span className="ra-callbar-k">Emergency · Call now</span>
          <strong className="ra-callbar-n">{formatPhone(phoneNumber)}</strong>
        </a>
      )}
    </div>
  )
}

export const REDALERT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800&family=Oswald:wght@500;600;700&display=swap');

.rt-theme--redalert{
  --ra-ink:#0d0e11;
  --ra-red:#e11d22;
  font-family:'Archivo', ui-sans-serif, system-ui, -apple-system, sans-serif;
}
.rt-theme--redalert .rt-ct-title,
.rt-theme--redalert .rt-ab-title,
.rt-theme--redalert .rt-services-title,
.rt-theme--redalert .rt-ab-gallery-h h3{
  font-family:'Oswald', sans-serif; font-weight:600; text-transform:uppercase; letter-spacing:.01em;
}
.rt-theme--redalert .ra-content{ background:var(--rt-paper); }
@media(max-width:899px){ .rt-theme--redalert{ padding-bottom:calc(64px + env(safe-area-inset-bottom,0px)); } }

/* Nav */
.ra-nav{ position:sticky; top:0; z-index:50; background:var(--ra-ink); }
.ra-nav-inner{ display:flex; align-items:center; gap:1rem; max-width:76rem; margin:0 auto; height:60px; padding:0 clamp(1rem,3vw,1.75rem); }
.ra-brand{ display:inline-flex; align-items:center; gap:.55rem; background:none; border:0; cursor:pointer; flex-shrink:0; }
.ra-brand-tile{ display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; border-radius:8px; overflow:hidden; background:var(--ra-red); color:#fff; font-family:'Oswald',sans-serif; font-weight:700; font-size:1.2rem; }
.ra-brand-tile img{ width:100%; height:100%; object-fit:cover; }
.ra-brand-name{ font-family:'Oswald',sans-serif; text-transform:uppercase; font-weight:600; font-size:1.12rem; color:#fff; letter-spacing:.02em; white-space:nowrap; }
.ra-links{ display:flex; align-items:stretch; gap:.15rem; margin-left:auto; height:100%; }
.ra-link{ position:relative; display:inline-flex; align-items:center; background:none; border:0; cursor:pointer; font-family:'Oswald',sans-serif; text-transform:uppercase; letter-spacing:.03em; font-weight:500; font-size:.98rem; color:rgba(255,255,255,.6); padding:0 .7rem; transition:color .15s ease; }
.ra-link:hover{ color:#fff; }
.ra-link.is-active{ color:var(--ra-red); }
.ra-link.is-active::after{ content:''; position:absolute; left:.7rem; right:.7rem; bottom:0; height:3px; background:var(--ra-red); }
.ra-nav-call{ flex-shrink:0; display:inline-flex; align-items:center; gap:.45rem; margin-left:.4rem; text-decoration:none; background:var(--ra-red); color:#fff; font-weight:700; font-size:.92rem; font-variant-numeric:tabular-nums; padding:.5rem .9rem; border-radius:9px; white-space:nowrap; }
.ra-nav-call:hover{ filter:brightness(1.08); }
@media(max-width:899px){
  .ra-nav-inner{ flex-wrap:wrap; height:auto; padding:.55rem .85rem; gap:.5rem; }
  .ra-nav-call{ display:none; }
  .ra-links{ order:3; flex-basis:100%; margin-left:0; height:auto; overflow-x:auto; scrollbar-width:none; }
  .ra-links::-webkit-scrollbar{ display:none; }
  .ra-link{ padding:.4rem .7rem .5rem; white-space:nowrap; }
}

/* Hazard emergency band */
.ra-alertbar{ overflow:hidden; background:var(--ra-red); color:#fff; border-top:2px solid rgba(255,255,255,.15); }
.ra-alertbar-track{ display:inline-block; white-space:nowrap; padding:.3rem 0; font-family:'Oswald',sans-serif; text-transform:uppercase; font-weight:600; font-size:.78rem; letter-spacing:.06em; animation:raMarquee 26s linear infinite; }
@keyframes raMarquee{ from{ transform:translateX(0); } to{ transform:translateX(-50%); } }
@media (prefers-reduced-motion:reduce){ .ra-alertbar-track{ animation:none; } }

/* Hero — dark full-bleed photo */
.ra-hero{ position:relative; overflow:hidden; background:var(--ra-ink); min-height:clamp(400px,58vh,560px); display:flex; align-items:flex-end; }
.ra-hero-bg{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.ra-hero-scrim{ position:absolute; inset:0; background:
  linear-gradient(180deg, rgba(10,11,14,.35) 0%, rgba(10,11,14,.35) 40%, rgba(10,11,14,.92) 100%),
  linear-gradient(90deg, rgba(225,29,34,.36) 0%, transparent 60%); }
.ra-hero-inner{ position:relative; width:100%; max-width:76rem; margin:0 auto; padding:clamp(2rem,6vw,4rem) clamp(1.25rem,4vw,1.75rem); color:#fff; }
.ra-eyebrow{ display:inline-flex; align-items:center; gap:.5rem; font-family:'Oswald',sans-serif; font-size:.82rem; font-weight:600; letter-spacing:.16em; text-transform:uppercase; color:#fff; }
.ra-dot{ width:9px; height:9px; border-radius:50%; background:var(--ra-red); box-shadow:0 0 0 4px rgba(225,29,34,.3); }
.ra-hero-title{ margin:.6rem 0 0; font-family:'Oswald',sans-serif; font-weight:700; text-transform:uppercase; line-height:.98; letter-spacing:.005em; font-size:clamp(2.6rem,8vw,5.25rem); text-wrap:balance; }
.ra-hero-sub{ margin:1rem 0 0; max-width:34rem; font-size:1.08rem; line-height:1.55; color:rgba(255,255,255,.85); }
.ra-hero-cta{ display:flex; flex-wrap:wrap; align-items:center; gap:.85rem; margin-top:clamp(1.4rem,3vw,2rem); }
.ra-call{ display:inline-flex; align-items:center; gap:.7rem; text-decoration:none; background:var(--ra-red); color:#fff; padding:.85rem 1.3rem; border-radius:12px; box-shadow:0 14px 30px -12px rgba(225,29,34,.7); transition:filter .15s ease, transform .15s ease; }
.ra-call:active{ transform:translateY(1px); }
.ra-call:hover{ filter:brightness(1.08); }
.ra-call-stack{ display:flex; flex-direction:column; align-items:flex-start; line-height:1.1; }
.ra-call-stack small{ font-size:.62rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase; opacity:.85; }
.ra-call-stack strong{ font-family:'Oswald',sans-serif; font-weight:600; font-size:1.5rem; font-variant-numeric:tabular-nums; }
.ra-ghost{ display:inline-flex; align-items:center; gap:.5rem; text-decoration:none; font-weight:700; font-size:.95rem; color:#fff; padding:.85rem 1.15rem; border-radius:12px; border:1.5px solid rgba(255,255,255,.35); }
.ra-ghost:hover{ background:rgba(255,255,255,.1); border-color:rgba(255,255,255,.6); }
@media(max-width:640px){ .ra-hero-cta{ flex-direction:column; align-items:stretch; } .ra-call, .ra-ghost{ justify-content:center; } }

.ra-main{ min-height:30vh; }

/* Mobile call bar */
.ra-callbar{ display:none; }
@media(max-width:899px){
  .ra-callbar{ display:flex; align-items:center; gap:.55rem; text-decoration:none; position:fixed; left:0; right:0; bottom:0; z-index:60; padding:.85rem 1.25rem calc(.85rem + env(safe-area-inset-bottom,0px)); background:var(--ra-red); color:#fff; box-shadow:0 -10px 30px -12px rgba(0,0,0,.4); }
  .ra-callbar-k{ font-size:.66rem; font-weight:700; letter-spacing:.12em; text-transform:uppercase; opacity:.85; }
  .ra-callbar-n{ margin-left:auto; font-family:'Oswald',sans-serif; font-weight:600; font-size:1.25rem; font-variant-numeric:tabular-nums; }
}
`
